type BuilderOptions = {
	get: (key: string) => unknown;
	set: (key: string, value: unknown) => void;
};

declare const _apiKey: string;

function normalizeTags(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	const out: string[] = [];
	for (let i = 0; i < value.length; i++) {
		const item = value[i];
		let text = '';
		if (typeof item === 'string') {
			text = item.trim();
		} else if (typeof item === 'object' && item !== null) {
			const record = item as Record<string, unknown>;
			if (typeof record.value === 'string') text = record.value.trim();
			else if (typeof record.title === 'string') text = record.title.trim();
			else if (typeof record.name === 'string') text = record.name.trim();
		}
		if (text.length > 0) out.push(text);
	}
	return out;
}

function normalizeStringList(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	const out: string[] = [];
	for (let i = 0; i < value.length; i++) {
		const item = value[i];
		if (typeof item === 'string' && item.trim().length > 0) {
			out.push(item.trim());
			continue;
		}
		if (typeof item === 'object' && item !== null) {
			const record = item as Record<string, unknown>;
			if (typeof record.value === 'string' && record.value.trim().length > 0) {
				out.push(record.value.trim());
			}
		}
	}
	return out;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toPlainJson<T>(value: T): T {
	if (value === undefined) return value;
	const json = JSON.stringify(value);
	return JSON.parse(json) as T;
}

function compact(value: unknown): unknown {
	if (value === undefined) return undefined;
	if (Array.isArray(value)) {
		const out: unknown[] = [];
		for (let i = 0; i < value.length; i++) {
			const next = compact(value[i]);
			if (next !== undefined) out.push(next);
		}
		return out;
	}
	if (!isRecord(value)) return value;

	const out: Record<string, unknown> = {};
	const keys = Object.keys(value);
	for (let i = 0; i < keys.length; i++) {
		const key = keys[i];
		const next = compact(value[key]);
		if (next !== undefined) out[key] = next;
	}
	return out;
}

function sanitizeSlide(value: unknown): Record<string, unknown> | null {
	const plainValue = toPlainJson(value);
	if (!isRecord(plainValue)) return null;

	const compacted = compact(plainValue);
	return isRecord(compacted) ? compacted : null;
}

function toImageSnapshot(value: unknown): Record<string, unknown> | null {
	if (!isRecord(value)) return null;
	if (typeof value.fileId !== 'string' || value.fileId.trim().length === 0) return null;

	return {
		fileId: value.fileId.trim(),
		...(typeof value.filePath === 'string' && value.filePath.trim().length > 0
			? { filePath: value.filePath.trim() }
			: {}),
		...(typeof value.croppingId === 'string' && value.croppingId.trim().length > 0
			? { croppingId: value.croppingId.trim() }
			: {}),
	};
}

function toSlideSnapshot(value: unknown): Record<string, unknown> | null {
	const slide = sanitizeSlide(value);
	if (!slide || typeof slide.title !== 'string' || slide.title.trim().length === 0) return null;

	const image = toImageSnapshot(slide.image);
	return {
		title: slide.title.trim(),
		...(image ? { image } : {}),
		...(typeof slide.imagePreview === 'string' ? { imagePreview: slide.imagePreview } : {}),
		...(typeof slide.altText === 'string' && slide.altText.trim().length > 0
			? { altText: slide.altText.trim() }
			: {}),
		...(typeof slide.text === 'string' && slide.text.trim().length > 0 ? { text: slide.text.trim() } : {}),
		...(typeof slide.icon === 'string' ? { icon: slide.icon } : {}),
		...(typeof slide.url === 'string' && slide.url.trim().length > 0 ? { url: slide.url.trim() } : {}),
		...(typeof slide.ctaLabel === 'string' && slide.ctaLabel.trim().length > 0
			? { ctaLabel: slide.ctaLabel.trim() }
			: {}),
		...(slide.hideCtaLabel === true ? { hideCtaLabel: true } : {}),
	};
}

function normalizeSlidesForCompare(value: unknown): Record<string, unknown>[] {
	if (!Array.isArray(value)) return [];
	const out: Record<string, unknown>[] = [];
	for (let i = 0; i < value.length; i++) {
		const slide = toSlideSnapshot(value[i]);
		if (slide) out.push(slide);
	}
	return out;
}

function sameJson(a: unknown, b: unknown): boolean {
	return JSON.stringify(a) === JSON.stringify(b);
}

function parseAutoGeneratedSlideKeysJson(value: unknown): string[] {
	if (typeof value !== 'string' || value.trim().length === 0) return [];
	try {
		return normalizeStringList(JSON.parse(value));
	} catch (_error) {
		console.warn('[TeaserCardSlider] Could not parse autoGeneratedSlideKeysJson');
		return [];
	}
}

function setAutoGeneratedSlideKeys(
	options: BuilderOptions,
	rawAutoGeneratedSlideKeysJson: unknown,
	keys: string[],
): void {
	const nextValue = JSON.stringify(keys);
	if (rawAutoGeneratedSlideKeysJson === nextValue) return;
	options.set('autoGeneratedSlideKeysJson', nextValue);
}

function restoreSlidesAfterProbe(options: BuilderOptions, existingSlidesSnapshot: unknown[]): void {
	try {
		options.set('slides', existingSlidesSnapshot);
	} catch (_restoreError) {
		console.warn('[TeaserCardSlider] Could not restore slides after probe');
	}
}

function trySetSlidesCandidate(
	options: BuilderOptions,
	candidate: unknown[],
	existingSlidesSnapshot: unknown[],
): true | unknown {
	try {
		options.set('slides', candidate);
		restoreSlidesAfterProbe(options, existingSlidesSnapshot);
		return true;
	} catch (error) {
		restoreSlidesAfterProbe(options, existingSlidesSnapshot);
		return error;
	}
}

function summarizeValue(value: unknown, depth = 0): unknown {
	if (depth >= 2) {
		if (Array.isArray(value)) return '[array]';
		if (isRecord(value)) return '[object]';
		return value;
	}

	if (Array.isArray(value)) {
		const out: unknown[] = [];
		for (let i = 0; i < value.length && i < 5; i++) {
			out.push(summarizeValue(value[i], depth + 1));
		}
		if (value.length > 5) out.push('…');
		return out;
	}

	if (!isRecord(value)) return value;

	const out: Record<string, unknown> = {};
	const keys = Object.keys(value);
	for (let i = 0; i < keys.length; i++) {
		out[keys[i]] = summarizeValue(value[keys[i]], depth + 1);
	}
	return out;
}

function debugRejectedSlides(
	options: BuilderOptions,
	finalSlides: unknown[],
	error: unknown,
	existingSlidesSnapshot: unknown[],
): void {
	console.error('[TeaserCardSlider] slides payload rejected', {
		error,
		slidesCount: finalSlides.length,
		summary: summarizeValue(finalSlides),
	});

	const emptyProbe = trySetSlidesCandidate(options, [], existingSlidesSnapshot);
	if (emptyProbe !== true) {
		console.error('[TeaserCardSlider] rejected empty slides array', { error: emptyProbe });
	}

	for (let i = 0; i < finalSlides.length; i++) {
		const slide = toSlideSnapshot(finalSlides[i]);
		if (!slide) {
			console.error('[TeaserCardSlider] slide is not a plain object', { index: i, value: finalSlides[i] });
			continue;
		}

		const minimalSlide: Record<string, unknown> = {
			title: slide.title,
			...(slide.hideCtaLabel === true ? { hideCtaLabel: true } : {}),
		};

		const minimalProbe = trySetSlidesCandidate(options, [minimalSlide], existingSlidesSnapshot);
		if (minimalProbe !== true) {
			console.error('[TeaserCardSlider] rejected base slide shape', {
				path: 'slides[' + i + ']',
				value: summarizeValue(minimalSlide),
				error: minimalProbe,
			});
		}

		const keys = Object.keys(slide);
		for (let j = 0; j < keys.length; j++) {
			const key = keys[j];
			if (key === 'title' || key === 'hideCtaLabel') continue;

			const probeSlide = toSlideSnapshot({ ...minimalSlide, [key]: slide[key] });
			const probeCandidate = probeSlide ? [probeSlide] : [];
			const probeResult = trySetSlidesCandidate(options, probeCandidate, existingSlidesSnapshot);
			if (probeResult !== true) {
				console.error('[TeaserCardSlider] rejected field', {
					path: 'slides[' + i + '].' + key,
					value: summarizeValue(slide[key]),
					error: probeResult,
				});

				if (key === 'image' && isRecord(slide[key])) {
					const imageValue = slide[key] as Record<string, unknown>;
					const imageKeys = Object.keys(imageValue);
					for (let k = 0; k < imageKeys.length; k++) {
						const imageKey = imageKeys[k];
						const imageProbe = toSlideSnapshot({
							...minimalSlide,
							image: {
								...(imageKey === 'fileId'
									? { fileId: imageValue[imageKey] }
									: { fileId: imageValue.fileId }),
								...(imageKey === 'filePath' ? { filePath: imageValue[imageKey] } : {}),
								...(imageKey === 'croppingId' ? { croppingId: imageValue[imageKey] } : {}),
							},
						});
						const imageProbeResult = trySetSlidesCandidate(
							options,
							imageProbe ? [imageProbe] : [],
							existingSlidesSnapshot,
						);
						if (imageProbeResult !== true) {
							console.error('[TeaserCardSlider] rejected nested field', {
								path: 'slides[' + i + '].image.' + imageKey,
								value: summarizeValue(imageValue[imageKey]),
								error: imageProbeResult,
							});
						}
					}
				}
			}
		}
	}
}

function currentSlidesSummary(options: BuilderOptions): unknown {
	return summarizeValue(normalizeSlidesForCompare(toPlainJson(options.get('slides'))));
}

function describeValue(value: unknown): Record<string, unknown> {
	return {
		type: typeof value,
		isArray: Array.isArray(value),
		constructor:
			value && typeof value === 'object' && (value as { constructor?: { name?: string } }).constructor
				? ((value as { constructor?: { name?: string } }).constructor?.name ?? 'unknown')
				: 'n/a',
		hasSet: !!(value && typeof (value as { set?: unknown }).set === 'function'),
		hasPush: !!(value && typeof (value as { push?: unknown }).push === 'function'),
		keys: isRecord(value) ? Object.keys(value) : [],
	};
}

function trySetValue(options: BuilderOptions, path: string, value: unknown, label: string): true | unknown {
	try {
		options.set(path, value);
		return true;
	} catch (error) {
		console.error('[TeaserCardSlider] ' + label + ' failed', {
			path,
			value: summarizeValue(value),
			error,
			currentSlides: currentSlidesSummary(options),
		});
		return error;
	}
}

function restoreSlidesAndThrow(options: BuilderOptions, existingSlidesSnapshot: unknown[], error: unknown): never {
	try {
		options.set('slides', existingSlidesSnapshot);
	} catch (_restoreError) {
		console.warn('[TeaserCardSlider] Could not restore slides after failed write');
	}
	throw error;
}

function setSlideField(options: BuilderOptions, existingSlidesSnapshot: unknown[], path: string, value: unknown): void {
	const result = trySetValue(options, path, value, 'field write');
	if (result !== true) restoreSlidesAndThrow(options, existingSlidesSnapshot, result);
}

function trySetLiveSlideField(options: BuilderOptions, index: number, key: string, value: unknown): true | unknown {
	const liveSlides = options.get('slides');
	if (!Array.isArray(liveSlides) || index >= liveSlides.length) {
		return new Error('Live slides array is not available for index ' + index);
	}

	const liveSlide = (liveSlides as unknown[])[index] as unknown;
	try {
		if (liveSlide && typeof (liveSlide as { set?: (k: string, v: unknown) => void }).set === 'function') {
			(liveSlide as { set: (k: string, v: unknown) => void }).set(key, value);
		} else if (liveSlide && typeof liveSlide === 'object') {
			(liveSlide as Record<string, unknown>)[key] = value;
		} else {
			return new Error('Live slide item is not writable at index ' + index);
		}
		return true;
	} catch (error) {
		console.error('[TeaserCardSlider] live field write failed', {
			path: 'slides[' + index + '].' + key,
			value: summarizeValue(value),
			error,
			liveSlide: describeValue(liveSlide),
			currentSlides: currentSlidesSummary(options),
		});
		return error;
	}
}

function tryPopulateSlidesViaLiveArray(
	options: BuilderOptions,
	finalSlides: Record<string, unknown>[],
): true | unknown {
	const liveSlides = options.get('slides');

	if (!Array.isArray(liveSlides)) {
		return new Error('Builder slides value is not an array');
	}

	try {
		while ((liveSlides as unknown[]).length > 0) {
			(liveSlides as { pop: () => unknown }).pop();
		}
	} catch (error) {
		console.error('[TeaserCardSlider] live array clear failed', {
			error,
			currentSlides: currentSlidesSummary(options),
		});
		return error;
	}

	for (let i = 0; i < finalSlides.length; i++) {
		const slide = finalSlides[i];

		let created = false;
		const createCandidates: unknown[] = [{}, Object.create(null), new Map<string, unknown>()];
		for (let j = 0; j < createCandidates.length; j++) {
			const candidate = createCandidates[j];
			try {
				(liveSlides as unknown[]).push(candidate);
				created = true;
				break;
			} catch (error) {
				console.error('[TeaserCardSlider] live item push failed', {
					index: i,
					candidate: describeValue(candidate),
					error,
					currentSlides: currentSlidesSummary(options),
				});
			}
		}

		if (!created) {
			return new Error('Could not create live slide item at index ' + i);
		}

		const liveWrites: Array<[string, unknown]> = [['title', slide.title]];
		if (slide.hideCtaLabel === true) liveWrites.push(['hideCtaLabel', true]);
		if (isRecord(slide.image)) liveWrites.push(['image', slide.image]);
		if (typeof slide.imagePreview === 'string') liveWrites.push(['imagePreview', slide.imagePreview]);
		if (typeof slide.altText === 'string') liveWrites.push(['altText', slide.altText]);
		if (typeof slide.text === 'string') liveWrites.push(['text', slide.text]);
		if (typeof slide.icon === 'string') liveWrites.push(['icon', slide.icon]);
		if (typeof slide.url === 'string') liveWrites.push(['url', slide.url]);
		if (typeof slide.ctaLabel === 'string') liveWrites.push(['ctaLabel', slide.ctaLabel]);

		for (let j = 0; j < liveWrites.length; j++) {
			const write = liveWrites[j];
			const writeResult = trySetLiveSlideField(options, i, write[0], write[1]);
			if (writeResult !== true) return writeResult;
		}
	}

	return true;
}

function tryPopulateSlidesIndividually(
	options: BuilderOptions,
	finalSlides: Record<string, unknown>[],
	existingSlidesSnapshot: unknown[],
): true | unknown {
	const clearResult = trySetValue(options, 'slides', [], 'clear slides array');
	if (clearResult !== true) return clearResult;

	for (let i = 0; i < finalSlides.length; i++) {
		const slide = finalSlides[i];
		const creationAttempts: Array<{ label: string; path: string; value: unknown }> = [
			{ label: 'direct title path write', path: 'slides.' + i + '.title', value: slide.title },
			{ label: 'direct title bracket-path write', path: 'slides[' + i + '].title', value: slide.title },
			{
				label: 'whole item dot-path write',
				path: 'slides.' + i,
				value: { title: slide.title, ...(slide.hideCtaLabel === true ? { hideCtaLabel: true } : {}) },
			},
			{
				label: 'whole item bracket-path write',
				path: 'slides[' + i + ']',
				value: { title: slide.title, ...(slide.hideCtaLabel === true ? { hideCtaLabel: true } : {}) },
			},
		];

		let created = false;
		for (let j = 0; j < creationAttempts.length; j++) {
			const attempt = creationAttempts[j];
			const attemptResult = trySetValue(options, attempt.path, attempt.value, attempt.label);
			if (attemptResult === true) {
				created = true;
				break;
			}
		}

		if (!created) {
			const liveSlides = options.get('slides');
			if (Array.isArray(liveSlides)) {
				try {
					(liveSlides as unknown[]).push({
						title: slide.title,
						...(slide.hideCtaLabel === true ? { hideCtaLabel: true } : {}),
					});
					created = true;
				} catch (error) {
					console.error('[TeaserCardSlider] live array push failed', {
						index: i,
						value: summarizeValue(slide),
						error,
						currentSlides: currentSlidesSummary(options),
					});
					return error;
				}
			}
		}

		if (!created) {
			return new Error('Could not create slide at index ' + i);
		}

		if (slide.hideCtaLabel === true)
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.hideCtaLabel', true);
		if (isRecord(slide.image)) {
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.image', slide.image);
		}
		if (typeof slide.imagePreview === 'string') {
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.imagePreview', slide.imagePreview);
		}
		if (typeof slide.altText === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.altText', slide.altText);
		if (typeof slide.text === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.text', slide.text);
		if (typeof slide.icon === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.icon', slide.icon);
		if (typeof slide.url === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.url', slide.url);
		if (typeof slide.ctaLabel === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.ctaLabel', slide.ctaLabel);
	}

	return true;
}

function setSlides(
	options: BuilderOptions,
	nextSlides: Record<string, unknown>[],
	existingSlides: unknown,
	existingSlidesSnapshot: unknown[],
): void {
	const sanitizedSlides = compact(toPlainJson(nextSlides));
	const inputSlides = Array.isArray(sanitizedSlides) ? sanitizedSlides : [];
	const finalSlides: Record<string, unknown>[] = [];
	for (let i = 0; i < inputSlides.length; i++) {
		const slide = toSlideSnapshot(inputSlides[i]);
		if (slide) finalSlides.push(slide);
	}
	const existingComparableSlides = normalizeSlidesForCompare(toPlainJson(existingSlides));
	if (sameJson(existingComparableSlides, finalSlides)) return;

	const baseSlides: Record<string, unknown>[] = [];
	for (let i = 0; i < finalSlides.length; i++) {
		baseSlides.push({
			title: finalSlides[i].title,
			...(finalSlides[i].hideCtaLabel === true ? { hideCtaLabel: true } : {}),
		});
	}

	const liveArrayResult = tryPopulateSlidesViaLiveArray(options, finalSlides);
	if (liveArrayResult === true) return;

	console.warn('[TeaserCardSlider] live array rebuild failed, falling back to direct set', {
		error: liveArrayResult,
		baseSlides: summarizeValue(baseSlides),
	});

	try {
		options.set('slides', baseSlides);
	} catch (error) {
		console.error('[TeaserCardSlider] rejected base slides set', {
			value: summarizeValue(baseSlides),
			error,
		});
		debugRejectedSlides(options, baseSlides, error, existingSlidesSnapshot);
		const fallbackResult = tryPopulateSlidesIndividually(options, finalSlides, existingSlidesSnapshot);
		if (fallbackResult !== true) restoreSlidesAndThrow(options, existingSlidesSnapshot, fallbackResult);
		return;
	}

	for (let i = 0; i < finalSlides.length; i++) {
		const slide = finalSlides[i];

		if (isRecord(slide.image)) {
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.image', slide.image);
		}
		if (typeof slide.imagePreview === 'string') {
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.imagePreview', slide.imagePreview);
		}
		if (typeof slide.altText === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.altText', slide.altText);
		if (typeof slide.text === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.text', slide.text);
		if (typeof slide.icon === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.icon', slide.icon);
		if (typeof slide.url === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.url', slide.url);
		if (typeof slide.ctaLabel === 'string')
			setSlideField(options, existingSlidesSnapshot, 'slides.' + i + '.ctaLabel', slide.ctaLabel);
	}
}

function getByPath(source: unknown, path: string): unknown {
	const segs = path.split('.');
	let cur: unknown = source;
	for (let i = 0; i < segs.length; i++) {
		if (typeof cur !== 'object' || cur === null) return undefined;
		cur = (cur as Record<string, unknown>)[segs[i]];
	}
	return cur;
}

function first(source: unknown, paths: string[]): unknown {
	for (let i = 0; i < paths.length; i++) {
		const value = getByPath(source, paths[i]);
		if (value !== undefined && value !== null) return value;
	}
	return undefined;
}

function str(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;
}

function findFirstDamImageBlock(node: unknown): Record<string, unknown> | undefined {
	function visit(value: unknown): Record<string, unknown> | undefined {
		if (Array.isArray(value)) {
			for (let i = 0; i < value.length; i++) {
				const found = visit(value[i]);
				if (found) return found;
			}
			return undefined;
		}

		if (!isRecord(value)) return undefined;
		if (getByPath(value, 'component.name') === 'DAMImage') return value;

		const fromOptions = visit(getByPath(value, 'component.options'));
		if (fromOptions) return fromOptions;

		const fromChildren = visit(getByPath(value, 'children'));
		if (fromChildren) return fromChildren;

		return undefined;
	}

	return visit(node);
}

function mapPage(entry: Record<string, unknown>): Record<string, unknown> | null {
	const title = str(first(entry, ['name']));
	if (!title) return null;

	const firstBlock = getByPath(entry, 'data.blocks.0');
	const damImageBlock = findFirstDamImageBlock(firstBlock);
	const damImageOptions = getByPath(damImageBlock, 'component.options');
	const imageValue = getByPath(damImageOptions, 'image');
	const altText = str(getByPath(damImageOptions, 'altText'));

	const slide: Record<string, unknown> = {
		title,
		hideCtaLabel: false,
		icon: '',
		ctaLabel: 'Read More',
	};
	// Keep the real `image` field reserved for the Builder DAM input type; the preview uses a JSON string so
	// `useDamFile` can parse it at runtime without breaking manual image selection in the editor.
	if (imageValue !== undefined) slide.imagePreview = JSON.stringify(imageValue);
	if (altText) slide.altText = altText;

	const text = str(first(entry, ['data.description']));
	if (text) slide.text = text;

	const pageUrl = str(first(entry, ['data.url']));
	if (pageUrl) slide.url = pageUrl;

	const sanitizedSlide = sanitizeSlide(slide);
	return sanitizedSlide && typeof sanitizedSlide.title === 'string' ? sanitizedSlide : null;
}

function slideKey(slide: Record<string, unknown>): string {
	const url = typeof slide.url === 'string' ? slide.url.trim().toLowerCase() : '';
	if (url) return 'url:' + url;
	const title = typeof slide.title === 'string' ? slide.title.trim().toLowerCase() : '';
	if (title) return 'title:' + title;
	return JSON.stringify(slide);
}

export function builderOnChangeTags(options: BuilderOptions): void {
	const rawSourceMode = options.get('sourceMode');
	const rawTags = options.get('tags');
	const maxItemsRaw = options.get('maxItems');
	const sortOrderRaw = options.get('sortOrder');
	const rawAutoGeneratedSlideKeysJson = options.get('autoGeneratedSlideKeysJson');
	const existingSlides = options.get('slides');

	const apiKey = _apiKey;
	if (!apiKey) {
		console.warn('[TeaserCardSlider] No Builder API key available – cannot fetch pages');
		return;
	}

	const existingSlidesSnapshot = Array.isArray(toPlainJson(existingSlides))
		? (toPlainJson(existingSlides) as unknown[])
		: [];
	const sourceMode = rawSourceMode === 'taggedPages' ? 'taggedPages' : 'manual';
	const tags = normalizeTags(rawTags);
	const existingSlidesList = existingSlidesSnapshot;
	const previousAutoGeneratedKeys = new Set<string>(parseAutoGeneratedSlideKeysJson(rawAutoGeneratedSlideKeysJson));
	const manualSlides: Record<string, unknown>[] = [];
	for (let i = 0; i < existingSlidesList.length; i++) {
		const slide = sanitizeSlide(existingSlidesList[i]);
		if (!slide || previousAutoGeneratedKeys.has(slideKey(slide))) continue;
		manualSlides.push(slide);
	}

	if (sourceMode !== 'taggedPages') {
		setSlides(options, manualSlides, existingSlides, existingSlidesSnapshot);
		setAutoGeneratedSlideKeys(options, rawAutoGeneratedSlideKeysJson, []);
		return;
	}

	if (tags.length === 0) {
		setSlides(options, manualSlides, existingSlides, existingSlidesSnapshot);
		setAutoGeneratedSlideKeys(options, rawAutoGeneratedSlideKeysJson, []);
		return;
	}

	const maxItems = Math.min(Math.max(Math.round(Number(maxItemsRaw) || 12), 1), 50);
	const sortOrder = sortOrderRaw === 'oldest' ? 'oldest' : 'newest';
	const query = JSON.stringify({
		$or: [{ tags: { $in: tags } }, { 'data.tags': { $in: tags } }],
	});

	const url =
		'https://cdn.builder.io/api/v3/content/page' +
		'?apiKey=' +
		encodeURIComponent(apiKey) +
		'&fields=' +
		encodeURIComponent('id,name,createdDate,data.url,data.tags,data.description,data.blocks[0]') +
		'&limit=' +
		maxItems +
		'&query=' +
		encodeURIComponent(query) +
		'&sort.createdDate=' +
		(sortOrder === 'newest' ? '-1' : '1') +
		'&enrich=true' +
		'&cachebust=true';

	fetch(url)
		.then(function (response) {
			return response.json();
		})
		.then(function (data: { results?: Record<string, unknown>[] }) {
			const pages: Record<string, unknown>[] = Array.isArray(data?.results) ? data.results : [];

			const newSlides: Record<string, unknown>[] = [];
			for (let i = 0; i < pages.length; i++) {
				const slide = mapPage(pages[i]);
				if (slide) newSlides.push(slide);
			}

			const result = manualSlides.slice();
			const keys = new Set<string>();
			for (let i = 0; i < manualSlides.length; i++) {
				keys.add(slideKey(manualSlides[i]));
			}
			for (let i = 0; i < newSlides.length; i++) {
				const key = slideKey(newSlides[i]);
				if (!keys.has(key)) {
					keys.add(key);
					result.push(newSlides[i]);
				}
			}
			setSlides(options, result, existingSlides, existingSlidesSnapshot);
			setAutoGeneratedSlideKeys(options, rawAutoGeneratedSlideKeysJson, newSlides.map(slideKey));
		})
		.catch(function (error: unknown) {
			console.error('[TeaserCardSlider] fetch failed', error);
		});
}

const serializedFunctions = [
	normalizeTags,
	normalizeStringList,
	isRecord,
	toPlainJson,
	compact,
	sanitizeSlide,
	toImageSnapshot,
	toSlideSnapshot,
	normalizeSlidesForCompare,
	sameJson,
	parseAutoGeneratedSlideKeysJson,
	setAutoGeneratedSlideKeys,
	restoreSlidesAfterProbe,
	trySetSlidesCandidate,
	summarizeValue,
	debugRejectedSlides,
	currentSlidesSummary,
	describeValue,
	trySetValue,
	restoreSlidesAndThrow,
	setSlideField,
	trySetLiveSlideField,
	tryPopulateSlidesViaLiveArray,
	tryPopulateSlidesIndividually,
	setSlides,
	getByPath,
	first,
	str,
	findFirstDamImageBlock,
	mapPage,
	slideKey,
	builderOnChangeTags,
];

export function createBuilderOnChangeTagsString(builderPublicApiKey: string): string {
	return (
		'var _apiKey = ' +
		JSON.stringify(builderPublicApiKey) +
		';\n' +
		serializedFunctions.map((fn) => fn.toString()).join('\n\n') +
		'\nreturn builderOnChangeTags.apply(this, arguments)'
	);
}
